import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ForgotPasswordService } from '../../services/forgot-password.service';
import { NgClass, NgIf } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppRoutes } from '@app/utils/enums/routes.enum';

@Component({
  selector: 'app-forgot-password-form',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, TranslatePipe, NgIf],
  templateUrl: './forgot-password-form.component.html',
  styleUrl: './forgot-password-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordFormComponent {
  public readonly success = output<string>();
  public readonly errorMessageKey = signal<string | null>(null);

  public readonly appRoutes = AppRoutes;

  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly forgotPasswordService = inject(ForgotPasswordService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly forgotPasswordForm = this.formBuilder.nonNullable.group({
    email: this.formBuilder.nonNullable.control('', [
      Validators.required,
      Validators.email,
      Validators.maxLength(255),
    ]),
  });

  public get emailControl(): FormControl<string> {
    return this.forgotPasswordForm.controls.email;
  }

  public onSubmit(): void {
    if (this.forgotPasswordForm.invalid) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    const email = this.emailControl.value;
    this.forgotPasswordService
      .sendForgotPasswordEmail(email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.success.emit(email),
        error: () => this.errorMessageKey.set('forgotPasswordPage.failureMessage'),
      });
  }

  public returnToLogin(): void {
    this.router.navigate([this.appRoutes.LOGIN]);
  }
}
